/**
 * Library tab — every voiceprint Meridian has on this device, with
 * enough metadata to inspect each one. Tap a row to drill into its
 * sample history (drives visibility into whether the rolling window
 * is cycling and what the per-sample durations look like).
 */
import { useCallback, useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  RotateCcw,
} from "lucide-react";

import {
  devListSamples,
  devListVoiceprints,
  devResetVoiceprintToEnrollment,
} from "@/hooks/useDevDiagnostics";
import type { DevVoiceprint, DevVoiceprintSample } from "@/types/devMode";

import {
  formatAbsoluteTime,
  formatDuration,
  formatRelativeTime,
  formatVectorPreview,
  truncateId,
} from "./devFormatters";

export function LibraryTab() {
  const [voiceprints, setVoiceprints] = useState<DevVoiceprint[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [samples, setSamples] = useState<Record<string, DevVoiceprintSample[]>>({});
  const [loadingSamples, setLoadingSamples] = useState<Set<string>>(new Set());
  const [resetConfirm, setResetConfirm] = useState<string | null>(null);
  const [resettingId, setResettingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const rows = await devListVoiceprints();
      setVoiceprints(rows);
    } catch (err) {
      setError(String(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleReset = useCallback(
    async (id: string) => {
      setResettingId(id);
      try {
        await devResetVoiceprintToEnrollment(id);
        // Drop cached samples so the row re-fetches after reset.
        setSamples((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        await refresh();
      } catch (err) {
        setError(String(err));
      } finally {
        setResettingId(null);
        setResetConfirm(null);
      }
    },
    [refresh]
  );

  const toggleExpand = useCallback(
    async (id: string) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      if (!samples[id]) {
        setLoadingSamples((prev) => new Set(prev).add(id));
        try {
          const rows = await devListSamples(id);
          setSamples((prev) => ({ ...prev, [id]: rows }));
        } catch {
          setSamples((prev) => ({ ...prev, [id]: [] }));
        } finally {
          setLoadingSamples((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      }
    },
    [samples]
  );

  if (error) {
    return <p className="p-4 text-sm text-red-400">Failed to load: {error}</p>;
  }

  if (!voiceprints) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-white/40">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading library…
      </div>
    );
  }

  if (voiceprints.length === 0) {
    return (
      <p className="p-6 text-center text-sm text-white/40">
        No voiceprints stored yet. Enroll yourself or resolve a provisional
        speaker to populate the library.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="flex items-center justify-between px-1">
        <p className="text-[11px] uppercase tracking-[0.2em] text-white/40">
          {voiceprints.length} voice{voiceprints.length === 1 ? "" : "s"}
        </p>
        <button
          onClick={refresh}
          className="flex items-center gap-1 text-[11px] text-white/50 active:opacity-60"
        >
          <RefreshCw className="h-3 w-3" />
          refresh
        </button>
      </div>

      {voiceprints.map((vp) => {
        const isOpen = expanded.has(vp.id);
        const vpSamples = samples[vp.id] ?? [];
        const isLoading = loadingSamples.has(vp.id);
        return (
          <div
            key={vp.id}
            className="rounded-lg border border-white/10 bg-white/[0.02]"
          >
            <button
              type="button"
              onClick={() => void toggleExpand(vp.id)}
              className="flex w-full items-start gap-2 p-3 text-left"
            >
              <div className="mt-0.5 text-white/40">
                {isOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-[14px] font-medium text-white">
                    {vp.id === "user_self" ? "user_self" : truncateId(vp.id)}
                  </p>
                  <span className="rounded-full bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-white/50">
                    {vp.sampleCount} sample{vp.sampleCount === 1 ? "" : "s"}
                  </span>
                </div>
                <p className="mt-0.5 text-[11px] text-white/40">
                  heard {formatRelativeTime(vp.lastHeardAt)} · enrolled{" "}
                  {formatRelativeTime(vp.enrolledAt)}
                </p>
                <p className="mt-1 font-mono text-[10px] text-white/30">
                  v[0..8] {formatVectorPreview(vp.vectorPreview)}
                </p>
                <p className="mt-0.5 text-[10px] text-white/30">
                  model {vp.modelVersion} · {vp.dim}-dim
                </p>
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-white/10 px-4 py-3">
                {isLoading ? (
                  <div className="flex items-center gap-2 text-[12px] text-white/40">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading samples…
                  </div>
                ) : vpSamples.length === 0 ? (
                  <p className="text-[12px] italic text-white/40">
                    No samples yet. First rolling adaptation will backfill the
                    enrollment centroid and add the new sample.
                  </p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {vpSamples.map((s, i) => (
                      <li
                        key={s.id}
                        className="flex flex-col gap-0.5 text-[11px]"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-white/80">
                            #{i + 1} · {formatDuration(s.durationSeconds)}
                          </span>
                          <span className="text-white/40">
                            {formatRelativeTime(s.recordedAt)}
                          </span>
                        </div>
                        <p className="text-white/40">
                          {formatAbsoluteTime(s.recordedAt)} · session{" "}
                          {truncateId(s.sessionId)}
                        </p>
                        <p className="font-mono text-[10px] text-white/30">
                          v[0..8] {formatVectorPreview(s.vectorPreview)}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}

                {vpSamples.length > 1 && (
                  <ResetAction
                    voiceprintId={vp.id}
                    confirming={resetConfirm === vp.id}
                    resetting={resettingId === vp.id}
                    onArm={() => setResetConfirm(vp.id)}
                    onCancel={() => setResetConfirm(null)}
                    onConfirm={() => void handleReset(vp.id)}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Reset-to-enrollment action with two-tap confirm ──

interface ResetActionProps {
  voiceprintId: string;
  confirming: boolean;
  resetting: boolean;
  onArm: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Destructive action on a voiceprint row — wipes rolling adaptations
 * and restores the pre-adaptation enrollment centroid. Requires a
 * two-tap confirm so an accidental press doesn't destroy adapt history.
 * Lives behind Dev Mode specifically because it's recovery, not
 * product UX: the day we ship this to real users it becomes a
 * Contacts-integrated "forget their voice + re-enroll" flow.
 */
function ResetAction({
  voiceprintId,
  confirming,
  resetting,
  onArm,
  onCancel,
  onConfirm,
}: ResetActionProps) {
  if (resetting) {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-md border border-amber-400/30 bg-amber-400/5 px-3 py-2 text-[11px] text-amber-200">
        <Loader2 className="h-3 w-3 animate-spin" />
        Resetting…
      </div>
    );
  }
  if (confirming) {
    return (
      <div className="mt-3 flex flex-col gap-2 rounded-md border border-amber-400/30 bg-amber-400/5 px-3 py-2 text-[11px]">
        <p className="text-amber-200/80">
          Wipe every rolling sample on{" "}
          <span className="font-mono text-amber-100">
            {voiceprintId === "user_self" ? "user_self" : truncateId(voiceprintId)}
          </span>{" "}
          and restore the pre-adaptation enrollment centroid?
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-md bg-white/5 py-1.5 text-[11px] text-white/70 active:bg-white/10"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 rounded-md bg-amber-400/80 py-1.5 text-[11px] font-medium text-black active:bg-amber-400"
          >
            Reset
          </button>
        </div>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onArm}
      className="mt-3 flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-1.5 text-[11px] text-white/60 active:bg-white/[0.05]"
    >
      <RotateCcw className="h-3 w-3" />
      Reset to enrollment
    </button>
  );
}
